import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { GpsStoredPoint } from './karta/gpsUtils';
import type { huntTrackUpload } from './api';

/**
 * Eget spår med skärmlåset på (iOS och Android). Webbvyns GPS pausas när skärmen låses; en inbyggd
 * platsspårare sparar positionerna under tiden och lämnar över dem när appen öppnas igen.
 * Under en jakt skickar den också spåret och positionen till servern medan webbvyn sover.
 * Nativ del: ios/App/App/BackgroundTrackPlugin.swift och android/…/BackgroundTrackPlugin.java
 * (förgrundstjänst med ständig notis), med samma API.
 */

type Upload = ReturnType<typeof huntTrackUpload>;

/** Pluginens händelselogg och läge (Profil → Bakgrundsspår). Tider i ms. */
export interface BackgroundTrackReport {
  events: { t: number; text: string }[];
  running: boolean;
  hunt: boolean;
  authorization: string;
  accuracy: string;
  lowPower: boolean;
  waiting: number;
  unsent: number;
  lastSend: number;
  lastSendResult: string;
  lastFix: number;
}

const BackgroundTrack = registerPlugin<{
  start(opts: { upload?: Upload | null }): Promise<{ started: boolean }>;
  stop(): Promise<void>;
  drain(): Promise<{ points: GpsStoredPoint[] }>;
  log(): Promise<BackgroundTrackReport>;
  addListener(event: 'resume', cb: () => void): Promise<PluginListenerHandle>;
}>('BackgroundTrack');

export function isBackgroundTrackAvailable(): boolean {
  const cap = (window as any).Capacitor;
  // Webben laddas ibland från qvenari.com, så äldre appbyggen utan pluginet får samma kod.
  const platform = cap?.getPlatform?.();
  return !!cap?.isNativePlatform?.() && (platform === 'ios' || platform === 'android') && !!cap.isPluginAvailable?.('BackgroundTrack');
}

/**
 * Startar (eller uppdaterar) inspelningen. Med `upload` skickas spåret till jakten medan appen
 * är i bakgrunden; anropa igen när appen kommer fram så att pluginen får en färsk inloggning.
 */
export function startBackgroundTrack(upload: Upload | null = null): void {
  if (!isBackgroundTrackAvailable()) return;
  BackgroundTrack.start({ upload }).catch(e => console.warn('[BackgroundTrack] kunde inte starta', e));
}

export function stopBackgroundTrack(): void {
  if (!isBackgroundTrackAvailable()) return;
  BackgroundTrack.stop().catch(() => {});
}

/** Punkterna som sparats medan appen inte var framme, äldst först. */
export async function drainBackgroundTrack(): Promise<GpsStoredPoint[]> {
  if (!isBackgroundTrackAvailable()) return [];
  try {
    return (await BackgroundTrack.drain()).points;
  } catch {
    return [];
  }
}

/** Appen kom fram igen (säkrare än visibilitychange, som webbvyn inte alltid skickar). */
export function onBackgroundTrackResume(cb: () => void): () => void {
  if (!isBackgroundTrackAvailable()) return () => {};
  const handle = BackgroundTrack.addListener('resume', cb);
  return () => { void handle.then(h => h.remove()).catch(() => {}); };
}

/** Händelseloggen, eller null i webbläsaren och i äldre appbyggen. */
export async function readBackgroundTrackLog(): Promise<BackgroundTrackReport | null> {
  if (!isBackgroundTrackAvailable()) return null;
  try {
    return await BackgroundTrack.log();
  } catch {
    return null;
  }
}
